import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TablerIconsModule } from 'angular-tabler-icons';

export interface SearchableMultiSelectOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-searchable-multi-select',
  standalone: true,
  imports: [CommonModule, FormsModule, TablerIconsModule],
  templateUrl: './searchable-multi-select.component.html',
  styleUrl: './searchable-multi-select.component.scss',
})
export class SearchableMultiSelectComponent {
  private searchInput?: ElementRef<HTMLInputElement>;
  private shouldFocusSearchInput = false;
  private shouldIgnoreNextOptionClick = false;

  @ViewChild('searchInput')
  set searchInputRef(input: ElementRef<HTMLInputElement> | undefined) {
    this.searchInput = input;

    if (input && this.shouldFocusSearchInput) {
      this.focusSearchInput();
    }
  }

  @Input() options: SearchableMultiSelectOption[] = [];
  @Input() selectedValues: string[] = [];
  @Input() placeholder = 'Todos os status';
  @Input() searchPlaceholder = 'Buscar status...';
  @Input() sectionTitle = 'Status';
  @Input() showSectionTitle = true;
  @Input() emptyMessage = 'Nenhum status encontrado.';
  @Input() selectedItemSingular = 'status selecionado';
  @Input() selectedItemPlural = 'status selecionados';
  @Input() selectedSummaryEmpty = 'Nenhum status selecionado';
  @Input() disabled = false;
  @Output() selectedValuesChange = new EventEmitter<string[]>();

  isOpen = false;
  searchTerm = '';

  constructor(private elementRef: ElementRef<HTMLElement>) {}

  get filteredOptions(): SearchableMultiSelectOption[] {
    const normalizedSearch = this.normalize(this.searchTerm);

    if (!normalizedSearch) {
      return this.options;
    }

    return this.options.filter((option) =>
      this.normalize(option.label).includes(normalizedSearch),
    );
  }

  get triggerLabel(): string {
    if (this.selectedValues.length === 0) {
      return this.placeholder;
    }

    if (this.selectedValues.length === 1) {
      return (
        this.options.find((option) => option.value === this.selectedValues[0])
          ?.label ?? this.selectedValues[0]
      );
    }

    return `${this.selectedValues.length} ${this.selectedItemPlural}`;
  }

  get footerLabel(): string {
    if (!this.selectedValues.length) {
      return this.selectedSummaryEmpty;
    }

    return `${this.selectedValues.length} ${
      this.selectedValues.length === 1
        ? this.selectedItemSingular
        : this.selectedItemPlural
    }`;
  }

  toggleOpen(): void {
    if (this.disabled) return;

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      this.focusSearchInput();
    } else {
      this.shouldFocusSearchInput = false;
    }
  }

  isSelected(value: string): boolean {
    return this.selectedValues.includes(value);
  }

  toggleValueFromPointer(value: string, event: PointerEvent): void {
    if (event.pointerType === 'mouse' && event.button !== 0) {
      return;
    }

    this.shouldIgnoreNextOptionClick = true;
    this.toggleValue(value, event);
  }

  toggleValueFromClick(value: string, event: MouseEvent): void {
    if (this.shouldIgnoreNextOptionClick) {
      this.shouldIgnoreNextOptionClick = false;
      event.stopPropagation();
      event.preventDefault();
      return;
    }

    this.toggleValue(value, event);
  }

  selectOnlyFilteredOptionFromKeyboard(event: Event): void {
    event.stopPropagation();
    event.preventDefault();

    if (this.disabled || this.filteredOptions.length !== 1) {
      return;
    }

    const value = this.filteredOptions[0].value;

    if (this.isSelected(value)) {
      this.focusSearchInput();
      return;
    }

    const selectedValues = [...this.selectedValues, value];
    this.selectedValues = selectedValues;
    this.selectedValuesChange.emit(selectedValues);
    this.focusSearchInput();
  }

  private toggleValue(value: string, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();

    if (this.disabled) return;

    const selectedValues = this.isSelected(value)
      ? this.selectedValues.filter((item) => item !== value)
      : [...this.selectedValues, value];

    this.selectedValues = selectedValues;
    this.selectedValuesChange.emit(selectedValues);
    this.focusSearchInput();
  }

  clearSelection(event?: MouseEvent): void {
    event?.stopPropagation();
    event?.preventDefault();

    if (this.disabled) return;

    this.selectedValues = [];
    this.selectedValuesChange.emit([]);
    this.focusSearchInput();
  }

  private focusSearchInput(): void {
    this.shouldFocusSearchInput = true;

    setTimeout(() => {
      const input = this.searchInput?.nativeElement;

      if (!this.isOpen || !input) {
        return;
      }

      input.focus({ preventScroll: true });
      input.select();
      this.shouldFocusSearchInput = false;
    });
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent): void {
    if (!this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
      this.shouldFocusSearchInput = false;
    }
  }

  private normalize(value: string): string {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }
}
